import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useRouteMatch } from 'react-router-dom';

import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../hooks/use-has-rate-shopper-access-level';
import { LogService } from '../../../services/log-service';
import { noop } from '../../../ui-components/default-props/noop';
import { BackendActionTypeIds } from './generated/action-type-ids';
import { getRuleActionTypes } from '../api/automation-rules';

type ActionTypeEntry = {
  actionTypeId: string | number;
  name: string;
  hasAccess: boolean;
};

type WorkflowsContextValue = {
  accessibleActionTypeIds: Map<number, string> | null;
  isZoomableDrawerOpen: boolean;
  workflowId: string | null;
  openZoomableWorkflowDrawer: (idOrNew: string | 'new') => void;
  closeZoomableWorkflowDrawer: () => void;
};

const WorkflowsContext = createContext<WorkflowsContextValue>({
  accessibleActionTypeIds: null,
  isZoomableDrawerOpen: false,
  workflowId: null,
  openZoomableWorkflowDrawer: noop,
  closeZoomableWorkflowDrawer: noop,
});

export const WorkflowsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessibleActionTypeIds, setAccessibleActionTypeIds] = useState<Map<number, string> | null>(null);
  const [isZoomableDrawerOpen, setIsZoomableDrawerOpen] = useState(false);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);

  const openZoomableWorkflowDrawer = useCallback((idOrNew: string | 'new') => {
    setWorkflowId(idOrNew === 'new' ? null : idOrNew);
    setIsZoomableDrawerOpen(true);
  }, []);

  const closeZoomableWorkflowDrawer = useCallback(() => {
    setIsZoomableDrawerOpen(false);
    setWorkflowId(null);
  }, []);

  const routeMatch = useRouteMatch<{ automationWorkflowId?: string }>('/settings/automationrules/:automationWorkflowId');

  useEffect(() => {
    const idParam = routeMatch?.params.automationWorkflowId;
    if (!idParam || isZoomableDrawerOpen) return;
    openZoomableWorkflowDrawer(idParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isZoomableDrawerOpen and openZoomableWorkflowDrawer intentionally omitted: effect is one-shot on URL param change, not on drawer state changes
  }, [routeMatch?.params.automationWorkflowId]);

  useEffect(() => {
    let cancelled = false;
    getRuleActionTypes()
      .then((actionTypes: ActionTypeEntry[]) => {
        if (cancelled) {
          return;
        }
        const map = actionTypes.reduce((acc, t) => {
          const actionTypeId = Number(t.actionTypeId);
          const isServiceCollection = actionTypeId === BackendActionTypeIds.ServiceCollection;
          const isAccessible = t.hasAccess && (!isServiceCollection || showRateShopper);
          if (isAccessible) {
            acc.set(actionTypeId, t.name);
          }
          return acc;
        }, new Map<number, string>());
        setAccessibleActionTypeIds(map);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        LogService.logError(new Error('Failed to load action types', { cause: err }));
        // Set to empty map so consumers unblock — actions simply won't appear
        setAccessibleActionTypeIds(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [showRateShopper]);

  return (
    <WorkflowsContext.Provider
      value={{
        accessibleActionTypeIds,
        isZoomableDrawerOpen,
        workflowId,
        openZoomableWorkflowDrawer,
        closeZoomableWorkflowDrawer,
      }}
    >
      {children}
    </WorkflowsContext.Provider>
  );
};

export const useWorkflowsContext = (): WorkflowsContextValue => useContext(WorkflowsContext);
